import { useState, useCallback } from 'react';
import { Settings, Loader2 } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useContractions, useContractionSettings, useContractionStats } from '../hooks';
import {
  SettingsSheet,
  TimerCard,
  PhaseProgressBar,
  HospitalBanner,
  EmergencyBanner,
  EmergencyButtons,
  ContractionHistory,
  ContractionInfoBox,
} from '../components';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { FeatureDisabled } from '@/components/FeatureDisabled';
import type { HospitalRule, NotificationMode, StatsUpdateInterval } from '../types';

export function ContractionTimerPage() {
  const { t } = useTranslation('contractionTimer');
  const isContractionCounterEnabled = useFeatureEnabled('feature_contraction_counter');
  const [showSettings, setShowSettings] = useState(false);

  // Hooks - must be called before any conditional returns
  const {
    contractions,
    activeContraction,
    isLoading: isContractionsLoading,
    error: contractionsError,
    startContraction,
    stopContraction,
    deleteContraction,
    deleteAllContractions,
    reportEmergencyEvent,
  } = useContractions();

  const {
    settings,
    isLoading: isSettingsLoading,
    hasSettings,
    saveSettings,
    updateSettings,
  } = useContractionSettings();

  // Calculate stats with current hospital rule
  const hospitalRule = settings?.hospital_rule || '5-1-1';
  const { stats, hospitalRuleCheck, emergencyTriggers, timeSinceLastContraction } =
    useContractionStats({
      contractions,
      hospitalRule,
    });

  // Show disabled message if feature is turned off
  if (!isContractionCounterEnabled) {
    return (
      <FeatureDisabled
        featureName={t('featureDisabled.name')}
        description={t('featureDisabled.description')}
      />
    );
  }

  // Show initial setup modal if no settings saved
  const showInitialSetup = !isSettingsLoading && !hasSettings;

  // Event handlers
  const handleStart = useCallback(async () => {
    await startContraction();
  }, [startContraction]);

  const handleStop = useCallback(async () => {
    if (activeContraction) {
      await stopContraction(activeContraction.id);
    }
  }, [activeContraction, stopContraction]);

  const handleSaveSettings = useCallback(
    async (input: {
      hospital_rule: HospitalRule;
      notification_mode: NotificationMode;
      stats_update_interval: StatsUpdateInterval;
    }) => {
      if (hasSettings) {
        return await updateSettings(input);
      } else {
        return await saveSettings(input);
      }
    },
    [hasSettings, saveSettings, updateSettings]
  );

  // Loading state
  if (isSettingsLoading || isContractionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  // Error state
  if (contractionsError) {
    return (
      <div className="p-4">
        <div className="glass-effect rounded-[15px] p-4 border-red-500/50">
          <p className="text-red-300 text-sm">{contractionsError}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-white text-2xl font-semibold">{t('pageTitle')}</h1>
        <button
          onClick={() => setShowSettings(true)}
          className="w-10 h-10 glass-effect rounded-xl flex items-center justify-center text-white"
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>

      {/* Main Content */}
      <div className="space-y-4">
        {/* Hospital Banner (if rule met) */}
        <HospitalBanner
          isMet={hospitalRuleCheck.isMet}
          rule={hospitalRule}
          details={hospitalRuleCheck.details}
        />

        {/* Emergency Banner (if triggers active) */}
        <EmergencyBanner triggers={emergencyTriggers} />

        {/* Timer Card */}
        <TimerCard
          activeContraction={activeContraction}
          stats={stats}
          timeSinceLastContraction={timeSinceLastContraction}
          isLoading={isContractionsLoading}
          onStart={handleStart}
          onStop={handleStop}
        />

        {/* Phase Progress Bar */}
        <PhaseProgressBar currentPhase={stats.phase} hasData={stats.count >= 2} />

        {/* Emergency Buttons */}
        <EmergencyButtons onReport={reportEmergencyEvent} />

        {/* Contraction History */}
        <ContractionHistory
          contractions={contractions}
          onDelete={deleteContraction}
          onDeleteAll={deleteAllContractions}
        />

        {/* Info Box */}
        <ContractionInfoBox initiallyExpanded={false} />
      </div>

      {/* Settings Sheet */}
      <SettingsSheet
        isOpen={showInitialSetup || showSettings}
        onClose={() => setShowSettings(false)}
        isInitialSetup={showInitialSetup}
        currentSettings={settings}
        onSave={handleSaveSettings}
      />
    </>
  );
}
